'use client'
import { BookingStatus } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/booking_status_pb'
import { Booking } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/booking_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import { BookingItemRecord } from '@fingertip/creator-proto/gen/fingertip/creator/booking/v1/booking_pb'
import { SquareArrowTopRightIcon, StepBackIcon } from '@fingertip/icons'
import { format, toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BookerSuccessCancelBookingDialog } from '@/components/booking/booker/dialog/booker-success-cancel-booking-dialog'
import { BookingLocation } from '@/components/booking/booking-location'
import { cancellationReasons } from '@/components/booking/booking-page/cancellation-reason-form'
import { Button } from '@/components/ui/button'
import { ResponsiveSheet } from '@/components/ui/responsive-sheet'
import { formatTimezoneLabel } from '@/lib/dates/time-zone'
import { useBookingItems } from '@/lib/hooks/use-booking-items'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useTimedDate } from '@/lib/hooks/use-timed-date'
import { RootStoreContext } from '@/lib/stores/root-store'
import { cn } from '@/lib/utils/cn'

const AddToCalendarButton = dynamic(
  () =>
    import('add-to-calendar-button-react').then((mod) => ({
      default: mod.AddToCalendarButton,
    })),
  {
    ssr: false,
  },
)

type Props = {
  booking: Booking
  bookingItems: BookingItemRecord[]
  siteSlug: string
  storeConfig?: StoreConfig
}

export const BookerStageBookingSuccess = observer(
  ({ booking, storeConfig, bookingItems, siteSlug }: Props) => {
    const breakpoint = useBreakpoint()
    const { t } = useTranslation()
    const [cancelSheetOpen, setCancelSheetOpen] = useState(false)
    const {
      bookerStore: { setStage, timeZone },
    } = useContext(RootStoreContext)

    const zonedStartTime = booking?.startTime
      ? toZonedTime(booking.startTime.toDate(), timeZone)
      : undefined

    const zonedEndTime = booking?.endTime
      ? toZonedTime(booking.endTime.toDate(), timeZone)
      : undefined

    const formattedDate = zonedStartTime
      ? format(zonedStartTime, 'EEEE, MMM do, yyyy', { timeZone })
      : undefined

    const formattedStartTime = zonedStartTime
      ? format(zonedStartTime, 'h:mm aaa', { timeZone })
      : undefined

    const formattedEndTime = zonedEndTime
      ? format(zonedEndTime, 'h:mm aaa', { timeZone })
      : undefined

    const addToCalendarConfig: any = {
      name: booking?.title || '',
      description: booking?.description,
      startDate: zonedStartTime
        ? format(zonedStartTime, 'yyyy-MM-dd', { timeZone })
        : undefined,
      startTime: zonedStartTime
        ? format(zonedStartTime, 'HH:mm', { timeZone })
        : undefined,
      endDate: zonedEndTime
        ? format(zonedEndTime, 'yyyy-MM-dd', { timeZone })
        : undefined,
      endTime: zonedEndTime
        ? format(zonedEndTime, 'HH:mm', { timeZone })
        : undefined,
      timeZone,
      location: booking?.location,
      options: ['Google', 'Apple', 'iCal', 'Outlook.com'],
    }

    const handleReschedule = useCallback(() => {
      setStage('SERVICES')
    }, [setStage])

    const handleCancel = useCallback(() => {
      setCancelSheetOpen(true)
    }, [setCancelSheetOpen])

    const bookingHref = useMemo(() => {
      if (booking?.id) {
        return `/${siteSlug}/booking?bookingId=${booking.id}&stage=SUCCESS`
      } else {
        return '/'
      }
    }, [booking?.id, siteSlug])

    const currentDate = useTimedDate()

    const cancelRescheduleButtonsShown = useMemo(() => {
      if (booking.startTime) {
        return currentDate.getTime() < booking.startTime.toDate().getTime()
      }
      return false
    }, [booking.startTime, currentDate])

    const cancellationFeeEnabled = useMemo(() => {
      return (
        !!booking.cancellationLateAfterDate &&
        booking.cancellationFeeInCents > 0
      )
    }, [booking.cancellationFeeInCents, booking.cancellationLateAfterDate])

    const cancellationFeeApplies = useMemo(() => {
      return (
        !!booking.cancellationLateAfterDate &&
        booking.cancellationFeeInCents > 0 &&
        currentDate.getTime() >
          booking.cancellationLateAfterDate.toDate().getTime()
      )
    }, [
      booking.cancellationFeeInCents,
      booking.cancellationLateAfterDate,
      currentDate,
    ])

    const cancellationLabel = useMemo(() => {
      const reason = cancellationReasons.find(
        (reason) => reason.value === booking.cancellationReason,
      )
      return reason ? `${reason.label}` : `${booking.cancellationReason}`
    }, [booking.cancellationReason])

    const rejectionLabel = useMemo(() => {
      const reason = cancellationReasons.find(
        (reason) => reason.value === booking.rejectionReason,
      )
      return reason ? `${reason.label}` : `${booking.rejectionReason}`
    }, [booking.rejectionReason])

    const summary = useBookingItems(bookingItems)

    const bookingItemsSummary = useMemo(() => {
      if (summary) {
        return summary.bookingItemsDescription
      }
    }, [summary])

    return (
      <div>
        <div className="mb-4 flex flex-col items-center justify-between">
          {booking?.status === BookingStatus.ACCEPTED && (
            <>
              <div className="max-w-[500px]">
                <Link href={bookingHref}>
                  <div className="h2 mb-4 flex flex-row items-center justify-center gap-2 text-foreground">
                    {t('this_event_is_scheduled')}
                    <SquareArrowTopRightIcon />
                  </div>
                </Link>
                <p className="mb-4 text-center text-foreground">
                  {t(
                    'we_emailed_you_and_the_other_attendees_a_calendar_invitation_with_all_the_details',
                  )}
                </p>
              </div>
              <AddToCalendarButton
                styleDark="border-radius: 50% 50%; --btn-background: rgb(249, 88, 40); --btn-border: rgb(249, 88, 40); --btn-text: #fff;"
                styleLight="border-radius: 50% 50%; --btn-background: rgb(249, 88, 40); --btn-border: rgb(249, 88, 40); --btn-text: #fff;"
                buttonStyle="default"
                trigger="click"
                hideIconButton
                hideBackground
                hideCheckmark
                hideBranding
                label={t('add_to_calendar')}
                lightMode="bodyScheme"
                {...addToCalendarConfig}
              />
            </>
          )}

          {booking?.status === BookingStatus.PENDING_CONFIRMATION && (
            <div className="max-w-[500px]">
              <Link href={bookingHref}>
                <div className="h2 mb-4 flex flex-row items-center justify-center gap-2 text-foreground">
                  {t('booking_requested')}
                  <SquareArrowTopRightIcon />
                </div>
              </Link>
              <p className="text-center text-foreground">
                Your booking request has been submitted for review. It has yet
                to be confirmed. You will receive a notification when the host
                confirms your booking.
              </p>
            </div>
          )}

          {booking?.status === BookingStatus.CANCELLED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('you_cancelled_this_e')}
              </h1>
              {!!cancellationLabel && (
                <div className="mt-4 flex flex-row gap-2">
                  <span>{t('Reason') + ': '}</span>
                  <span className="italic text-muted-foreground">
                    {cancellationLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {booking?.status === BookingStatus.REJECTED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('event_cancelled_by_h')}
              </h1>
              {!!rejectionLabel && (
                <div className="mt-2">
                  <span className="font-semibold">
                    {t('common:host') + ': '}
                  </span>
                  <span className="italic text-muted-foreground">
                    {rejectionLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {booking?.status === BookingStatus.COMPLETED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('this_event_has_compl')}
              </h1>
            </div>
          )}

          {booking?.status === BookingStatus.REFUNDED && (
            <div className="max-w-[500px]">
              <h1 className="h2 mb-4 text-center text-foreground">
                {t('this_event_has_been_')}
              </h1>
            </div>
          )}

          <div className="divider" />

          <div
            className={cn(
              'grid grid-cols-3 gap-x-2 gap-y-8 p-4 text-left text-foreground opacity-70 pointer-events-none',
              {
                'opacity-100 pointer-events-auto':
                  booking?.status === BookingStatus.ACCEPTED &&
                  cancelRescheduleButtonsShown,
              },
            )}
          >
            <div>{t('what')}</div>
            <div className="col-span-2">
              <p>{booking?.title}</p>
              {!!bookingItemsSummary && (
                <p className="opacity-70">{bookingItemsSummary}</p>
              )}
            </div>

            <div>{t('when')}</div>
            <div
              className={cn('col-span-2', {
                'line-through':
                  booking?.status === BookingStatus.CANCELLED ||
                  booking?.status === BookingStatus.REJECTED ||
                  booking?.status === BookingStatus.REFUNDED,
              })}
            >
              <p>{formattedDate}</p>
              <p>{`${formattedStartTime} – ${formattedEndTime}`}</p>
              <p>{formatTimezoneLabel(timeZone)}</p>
            </div>

            <div>{t('where')}</div>
            <div className="col-span-2">
              <BookingLocation
                location={booking?.location}
                locationType={booking?.response?.locationType}
                hideDetails={booking?.status !== BookingStatus.ACCEPTED}
              />
            </div>

            <div>{t('who')}</div>
            <div className="col-span-2">
              {booking?.response?.name && <p>{booking.response.name}</p>}
              {booking?.response?.email && <p>{booking.response.email}</p>}
              {(booking?.response?.guests || []).map((guestEmail) => {
                return (
                  <p key={guestEmail} className="opacity-70">
                    {guestEmail}
                  </p>
                )
              })}
            </div>

            {booking?.response?.notes &&
              booking?.response?.notes.length > 0 && (
                <>
                  <div>{t('notes')}</div>
                  <div className="col-span-2">
                    <p className="whitespace-pre-wrap">
                      {booking?.response?.notes}
                    </p>
                  </div>
                </>
              )}

            {/* This section is for host details */}
            {storeConfig?.hostDetails &&
              storeConfig?.hostDetails.length > 0 && (
                <>
                  <div>{t('host')}</div>
                  <div className="col-span-2">
                    <p className="whitespace-pre-wrap">
                      {storeConfig?.hostDetails}
                    </p>
                  </div>
                </>
              )}
          </div>

          <div className="divider" />

          {cancelRescheduleButtonsShown &&
            (booking?.status === BookingStatus.PENDING_CONFIRMATION ||
              booking?.status === BookingStatus.ACCEPTED) && (
              <div className="flex flex-col">
                <div className="mb-4 text-center text-muted-foreground">
                  <div className="mb-4">{t('need_to_make_a_change')}</div>
                  {cancellationFeeEnabled &&
                    !cancellationFeeApplies &&
                    booking.cancellationLateAfterDate && (
                      <div className="mb-4 flex flex-col">
                        <span>{t('cancel_for_free_befo')}</span>
                        <span>
                          {format(
                            booking.cancellationLateAfterDate.toDate(),
                            `EEE, MMM do, yyyy 'at' h:mm aaa`,
                          )}
                        </span>
                      </div>
                    )}
                </div>

                <div className="flex items-center justify-center space-x-2">
                  <Button
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    {t('cancel')}
                  </Button>

                  {booking?.status === BookingStatus.ACCEPTED &&
                    !booking.disableReschedule && (
                      <Button size="sm" onClick={handleReschedule}>
                        {t('change_reschedule')}
                      </Button>
                    )}
                </div>
              </div>
            )}
          <div className="mt-4 flex w-full flex-row justify-center">
            <Button size="sm" variant="secondary" asChild>
              <Link href={`/${siteSlug}`}>
                <div className="flex items-center">
                  <StepBackIcon className="mr-2 size-4" />
                  {t('Back to page')}
                </div>
              </Link>
            </Button>
          </div>
        </div>

        <ResponsiveSheet
          open={cancelSheetOpen}
          modal={breakpoint !== 'sm'}
          onOpenChange={(open) => {
            setCancelSheetOpen(open)
          }}
        >
          <BookerSuccessCancelBookingDialog
            booking={booking}
            storeConfig={storeConfig}
            siteSlug={siteSlug}
            cancellationFeeEnabled={cancellationFeeEnabled}
            cancellationFeeApplies={cancellationFeeApplies}
            onClose={() => {
              setCancelSheetOpen(false)
            }}
          />
        </ResponsiveSheet>
      </div>
    )
  },
)
